import find from 'lodash/find';
import map from 'lodash/map';
import filter from 'lodash/filter';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import sortBy from 'lodash/sortBy';
import type { TranslationKey } from '../i18n/i18n';
import type { ImageKey } from '../assets/Assets';
import type { MobilityValues } from './Mobility';
import type { Score } from './Score';
import concepts from './concepts.json';

export type ConceptKey =
	| 'parkingCarReduced'
	| 'mobilityStations'
	| 'speedlimit20'
	| 'parkingBikeIncreased'
	| 'electricCarChargers'
	| 'coworking'
	| 'showers'
	| 'carSharing'
	| 'parkingShared'
	| 'parcelShops'
	| 'fishies'
	| string;

export type CategoryKey =
	| 'category.driving'
	| 'category.parking'
	| 'category.off-the-street'
	| string;

export type RequirementKey =
	| 'capacityLoad'
	| 'capacityPersons'
	| 'timeToDrive'
	| 'reliability'
	| 'combinability'
	| 'weatherProtection'
	| 'travelDuration'
	| 'activity'
	| 'customizabilityPrestige'
	| 'costEfficency'
	| 'environment'
	| 'qualityOfLife';

export interface DefaultReactions {
	readonly veryPositive: TranslationKey[];
	readonly positive: TranslationKey[];
	readonly neutral: TranslationKey[];
	readonly negative: TranslationKey[];
	readonly veryNegative: TranslationKey[];
}

export interface Texts {
	readonly name: TranslationKey;
	readonly shortDescription: TranslationKey;
	readonly description: TranslationKey;
	readonly proArgument: TranslationKey;
	readonly contraArgument: TranslationKey;
	readonly resultTitle: TranslationKey;
	readonly resultText: TranslationKey;
}

export interface OverlayImage {
	readonly key: ImageKey;
	/**
	 * Priority of the overlay.
	 * The display order of the overlays depends on their priority.
	 * The higher the value, the further in the foreground the image is displayed.
	 */
	readonly priority?: number;
}

interface InActiveOverlayImages {
	readonly active?: OverlayImage;
	readonly inactive?: OverlayImage;
}

export interface Images {
	readonly icon: ImageKey;
	readonly overlay?: {
		readonly map?: InActiveOverlayImages;
		readonly persona?: InActiveOverlayImages;
	};
	readonly before: ImageKey;
	readonly after: ImageKey;
}

export type Concept = {
	readonly name: ConceptKey;
	readonly category: CategoryKey;
	readonly defaultReactions?: DefaultReactions;
	readonly texts: Texts;
	readonly images: Images;
} & {
	readonly [K in RequirementKey]: MobilityValues;
};

export type Concepts = Concept[];

export const getConcepts = (): Concepts => concepts;

export const isSameConcept = (
	concept1: Concept,
	concept2?: Concept
): boolean => {
	return concept1.name === concept2?.name;
};

export const findConceptByName = (name: ConceptKey): Concept | undefined => {
	return find(getConcepts(), ['name', name]);
};

export const containsConcept = (
	concepts: Concepts,
	concept: Concept
): boolean => {
	return !!find(concepts, ['name', concept.name]);
};

export const containsConceptByKey = (
	concepts: Concepts,
	conceptKey: ConceptKey
): boolean => {
	return !!find(concepts, ['name', conceptKey]);
};

export const excludeConcept = (
	concepts: Concepts,
	exclude?: Concept
): Concepts => {
	return filter(concepts, (concept) => !isSameConcept(concept, exclude));
};

export const getConceptsByCategory = (
	concepts: Concepts,
	category: CategoryKey
): Concepts => {
	return filter(concepts, ['category', category]);
};

export const getCategories = (): CategoryKey[] => {
	return uniq(map(getConcepts(), 'category'));
};

/**
 * Returns the image keys of the overlays according to their priorities (with highest priority last).
 */
export const getOverlayImages = (overlays: OverlayImage[]): ImageKey[] => {
	console.log('Overlay', overlays);
	return map(
		sortBy(compact(overlays), (overlay) => overlay.priority ?? 0),
		(overlay) => overlay.key
	);
};

export const getConceptScore = (concept: Concept): Score => {
	const sumValues = (values: MobilityValues): number =>
		values.car + values.bus + values.bike;
	return {
		environment: sumValues(concept.environment) * 10,
		happiness: undefined,
		qualityOfLife: sumValues(concept.qualityOfLife) * 10
	};
};
